import CreatableTags from 'Components/Common/CreatableTags'
import { update } from 'lodash'
import React, { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { toast } from 'react-toastify'
import { Col, Row } from 'reactstrap'
import { createCampaignScreeningQuestion, deleteCampaignScreeningQuestion, updateCampaignScreeningQuestion } from 'services/api/campaign'
import { fetchCampaignScreeningQuestionsThunk } from 'slices/campaigns/details/thunk'
import { AppDispatch } from 'store'
import { ScreeningQuestion as ScreeningQuestionInterface, ScreeningQuestionType } from 'types/campaign/details'
import { getErrorMessage } from 'utils/helpers'


interface ScreeningQuestionProp {
    screeningQuestion: ScreeningQuestionInterface,
    campaignId: string,
    onDeleteUnsaved: () => void
}

const ScreeningQuestion: React.FC<ScreeningQuestionProp> = ({ screeningQuestion, campaignId, onDeleteUnsaved }) => {

    const [screeningQuestionState, setScreeningQuestionState] = React.useState<ScreeningQuestionInterface>(screeningQuestion);
    const [updating, setUpdating] = React.useState(false);
    const [deleting, setDeleting] = React.useState(false);
    const dispatch: AppDispatch = useDispatch();
    const onChange = (updatedQuestion: ScreeningQuestionInterface) => {
        setScreeningQuestionState(updatedQuestion);
    }

    const selectedAgencyId = useSelector((state: any) => state.auth.selectedAgencyId);

    useEffect(() => {
        setScreeningQuestionState(screeningQuestion);
    }, [screeningQuestion]);

    const [editMode, setEditMode] = React.useState(false);

    useEffect(() => {
        if (!screeningQuestionState?.id) {
            setEditMode(true);
        }
    }, [screeningQuestionState?.question]);

    const updateScreeningQuestion = async () => {
        if (!updating && campaignId && selectedAgencyId && screeningQuestionState.id) {
            if(screeningQuestionState.type === ScreeningQuestionType.CHOICE && !screeningQuestionState.options?.length) {
                toast.error("Please add at least one option");
                return;
            }
            setUpdating(true);
            try {
                await updateCampaignScreeningQuestion(selectedAgencyId, campaignId, String(screeningQuestionState.id), screeningQuestionState);
                await dispatch(fetchCampaignScreeningQuestionsThunk(campaignId));
            } catch (error) {
                console.error("Failed to update screening question:", error);
                toast.error(getErrorMessage(error, "Failed to update screening question"));
            } finally {
                setUpdating(false);
                setEditMode(false);
            }
        }
    }

    const deleteScreeningQuestion = async () => {
        if (!deleting && campaignId && selectedAgencyId && screeningQuestionState.id) {
            setDeleting(true);
            try {
                await deleteCampaignScreeningQuestion(selectedAgencyId, campaignId, String(screeningQuestionState.id));
                await dispatch(fetchCampaignScreeningQuestionsThunk(campaignId));
            } catch (error) {
                console.error("Failed to delete screening question:", error);
                toast.error(getErrorMessage(error, "Failed to delete screening question"));
            } finally {
                setDeleting(false);
            }
        }
    }

    const createScreeningQuestion = async () => {
        if (!updating && campaignId && selectedAgencyId) {
            if(screeningQuestionState.type === ScreeningQuestionType.CHOICE && !screeningQuestionState.options?.length) {
                toast.error("Please add at least one option");
                return;
            }
            setUpdating(true);
            try {
                await createCampaignScreeningQuestion(selectedAgencyId, campaignId, screeningQuestionState);
                await dispatch(fetchCampaignScreeningQuestionsThunk(campaignId));
            } catch (error) {
                console.error("Failed to create screening question:", error);
                toast.error(getErrorMessage(error, "Failed to create screening question"));
            } finally {
                setUpdating(false);
                setEditMode(false);
            }
        }
    }

    const handleSave = () => {
        if (screeningQuestionState.id) {
            updateScreeningQuestion();
        } else {
            createScreeningQuestion();
        }
    }

    const handleDelete = () => {
        if (screeningQuestionState.id) {
            deleteScreeningQuestion();
        } else {
            onDeleteUnsaved();
        }
    }

    return (
        <div>
            <Row className='mt-3'>
                <Col sm={12}>
                    {
                        editMode ? (
                            <div className='screening-question'>

                                <div className="form-group">
                                    <label htmlFor="screening-question">Your Question</label>
                                    <input
                                        type="text"
                                        value={screeningQuestionState.question}
                                        onChange={(e) => {
                                            onChange({ ...screeningQuestionState, question: e.target.value })
                                        }}
                                        className="form-control"
                                        placeholder="Example: How many kids do you have?" />
                                </div>
                                <div className="form-group mt-3">
                                    <label htmlFor="screening-question">Question Type</label>
                                    <select
                                        value={screeningQuestionState.type}
                                        onChange={(e) => {
                                            onChange({ ...screeningQuestionState, type: e.target.value as ScreeningQuestionType })
                                        }}
                                        className="form-control"
                                    >
                                        <option value="">Select Question Type</option>
                                        <option value="text">Text</option>
                                        <option value="choice">Multiple Choice</option>
                                        <option value="binary">Yes/No</option>
                                    </select>
                                </div>
                                {
                                    screeningQuestionState.type === ScreeningQuestionType.CHOICE && (
                                        <div className="form-group mt-3">
                                            <label htmlFor="screening-question-options">Options (comma separated)</label>
                                            <CreatableTags
                                                splitOnSpaceAndComma={false}
                                                noOptionsMessage='Type to add options'
                                                placeholder='Add options'
                                                values={screeningQuestionState.options ?? []}
                                                onChange={(options) => {
                                                    onChange({ ...screeningQuestionState, options })
                                                }}
                                            />
                                        </div>
                                    )
                                }
                                <div className="d-flex align-items-center justify-content-end mt-3 gap-2">
                                    <button className='btn btn-sm btn-outline-danger' disabled={deleting} onClick={handleDelete}>
                                        {
                                            deleting ? 'Deleting...' : 'Delete Question'
                                        }
                                    </button>
                                    <button disabled={!screeningQuestionState.question || !screeningQuestionState.type || updating} className='btn btn-sm btn-gradient' onClick={() => handleSave()}>
                                        {
                                            updating ? 'Saving...' : 'Save Question'
                                        }
                                    </button>
                                </div>
                            </div>

                        ) : (
                            <div className='screening-question'>
                                <div className='d-flex justify-content-between gap-3 align-items-center'>
                                    <div className='d-flex justify-content-between gap-3 align-items-center'>
                                        <div className="screening-question-icon">
                                            <i className='ri-question-line'></i>
                                        </div>
                                        <div className='d-flex flex-column'>
                                            <h5 className='mb-0'>{screeningQuestionState.question}</h5>
                                            <span className='screening-question-type'>
                                                {
                                                    screeningQuestionState.type === ScreeningQuestionType.TEXT ? 'Text' : (screeningQuestionState.type === ScreeningQuestionType.BINARY ? 'Yes/No' : 'Multiple Choice')
                                                } Question {screeningQuestionState.type === ScreeningQuestionType.CHOICE ? `(${screeningQuestionState.options?.join(', ')})` : ''}
                                            </span>
                                        </div>
                                    </div>
                                    <div className='d-flex align-items-center justify-content-end gap-2'>
                                        <button className='unset-all' onClick={handleDelete}>
                                            <span>
                                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                    <path d="M19.3006 9.29883L17.5051 19.5115C17.3538 20.3723 16.6062 21 15.7324 21H8.4689C7.59505 21 6.84742 20.3723 6.6961 19.5115L4.90063 9.29883" stroke="black" strokeLinecap="round" strokeLinejoin="round" />
                                                    <path d="M20.2 6.60037H15.1375M4 6.60037H9.0625M9.0625 6.60037V4.80018C9.0625 3.80597 9.86839 3 10.8625 3H13.3375C14.3316 3 15.1375 3.80597 15.1375 4.80018V6.60037M9.0625 6.60037H15.1375" stroke="black" strokeLinecap="round" strokeLinejoin="round" />
                                                </svg>
                                            </span>
                                        </button>
                                        <button className='unset-all text-primary' onClick={() => setEditMode(true)}>Edit</button>
                                    </div>
                                </div>
                            </div>
                        )
                    }
                </Col>
            </Row>
        </div>
    )
}

export default ScreeningQuestion
